"use client";

import { ArrowRight, Sparkles, Flame } from "lucide-react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLuckyWheelDynamics, useLuckyWheelGrandPrize } from "../../hooks/useLuckyWheelDynamics";
import DynamicsScroll from "./DynamicsScroll";
import GrandPrizeDisplay from "./GrandPrizeDisplay";

interface OdinForgeBannerProps {
	language: "zh" | "en";
}

export default function OdinForgeBanner({ language }: OdinForgeBannerProps) {
	const { messages, isLoading: isDynamicsLoading } = useLuckyWheelDynamics();
	const { grandPrize, isLoading: isGrandPrizeLoading } = useLuckyWheelGrandPrize(language);
	const router = useRouter();

	const translations = {
		zh: {
			title: "奥丁宝符炉",
			enterGame: "进入游戏",
		},
		en: {
			title: "Odin Forge",
			enterGame: "Enter Game",
		},
	};

	const t = translations[language];

	const handleEnterGame = () => {
		router.push("/plaza/lucky-wheel");
	};

	return (
		<Card className="rounded-2xl bg-light-surface dark:bg-dark-surface border-light-border dark:border-dark-border shadow-md overflow-hidden relative group">
			{/* 背景装饰动效 - 橙色系 */}
			<div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-orange-500/5 rounded-full blur-2xl group-hover:bg-orange-500/10 transition-all duration-700" />
			<div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl group-hover:bg-amber-500/10 transition-all duration-700" />

			<CardContent className="p-3 relative z-10">
				{/* 标题栏 */}
				<div className="flex items-center justify-between mb-3 px-1">
					<h2 className="text-base font-bold text-light-text dark:text-dark-text flex items-center gap-2">
						<div className="bg-gradient-to-br from-orange-500 to-amber-600 p-1.5 rounded-lg">
							<Flame className="w-3.5 h-3.5 text-white" />
						</div>
						{t.title}
					</h2>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleEnterGame}
						className="h-7 px-2 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 cursor-pointer"
					>
						<span className="text-xs">{t.enterGame}</span>
						<ArrowRight className="w-3.5 h-3.5 ml-1" />
					</Button>
				</div>

				{/* 第一部分：大奖展示 */}
				<div className="mb-4">
					<GrandPrizeDisplay 
						grandPrize={grandPrize} 
						isLoading={isGrandPrizeLoading} 
						language={language} 
					/>
				</div>

				{/* 分割线 */}
				<div className="relative h-px w-full bg-gradient-to-r from-transparent via-light-border dark:via-dark-border to-transparent mb-4">
					<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-light-surface dark:bg-dark-surface px-2 text-[9px] text-gray-400 dark:text-gray-500 font-bold tracking-wider flex items-center gap-1">
						<Sparkles className="w-2.5 h-2.5 opacity-50" />
						FORGE FEED
					</div>
				</div>

				{/* 第二部分：动态记录 */}
				<div className="w-full">
					<div className="h-[260px] sm:h-[300px] p-1.5 rounded-xl bg-light-background/50 dark:bg-black/20 border border-light-border dark:border-white/5">
						<DynamicsScroll
							messages={messages}
							isLoading={isDynamicsLoading}
							language={language}
						/>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
